import { markdown } from '@codemirror/lang-markdown';
import { RangeSetBuilder } from '@codemirror/state';
import {
  Decoration,
  EditorView,
  ViewPlugin,
  type ViewUpdate,
} from '@codemirror/view';

const HANDLEBARS_RE = /\{\{[\s\S]*?\}\}/g;

/** Matches a helper name: at start after {{ or nested after ( e.g. {{FORMAT (VALUE ...) }} */
const HELPER_NAME_RE = /(?:\{\{|\()\s*([A-Za-z_][A-Za-z0-9_]*)/g;
/** Double-quoted string (allows escaped " inside) */
const STRING_RE = /"[^"\\]*(?:\\.[^"\\]*)*"/g;
/** Number (integer or decimal) */
const NUMBER_RE = /\b\d+(?:\.\d+)?\b/g;
/** Hash param name: word immediately followed by = */
const PARAM_RE = /\b([A-Za-z_][A-Za-z0-9_]*)\s*=/g;

type DecorationRange = { from: number; to: number; deco: Decoration };

function buildDecorations(view: EditorView) {
  const ranges: DecorationRange[] = [];
  const text = view.state.doc.toString();

  let match: RegExpExecArray | null;
  while ((match = HANDLEBARS_RE.exec(text))) {
    const blockStart = match.index;
    const blockEnd = blockStart + match[0].length;
    const blockContent = match[0];
    const innerStart = blockStart + 2;

    // Brackets {{ and }}
    ranges.push({
      from: blockStart,
      to: blockStart + 2,
      deco: Decoration.mark({ class: 'cm-hbsBracket' }),
    });
    ranges.push({
      from: blockEnd - 2,
      to: blockEnd,
      deco: Decoration.mark({ class: 'cm-hbsBracket' }),
    });

    // Nested parens ( and ) inside the block
    const inner = blockContent.slice(2, blockContent.length - 2);
    for (let i = 0; i < inner.length; i++) {
      if (inner[i] === '(' || inner[i] === ')') {
        ranges.push({
          from: innerStart + i,
          to: innerStart + i + 1,
          deco: Decoration.mark({ class: 'cm-hbsParen' }),
        });
      }
    }

    // Helper names (top-level after {{ or nested after ()
    HELPER_NAME_RE.lastIndex = 0;
    let helperMatch: RegExpExecArray | null;
    while ((helperMatch = HELPER_NAME_RE.exec(blockContent))) {
      const helperName = helperMatch[1];
      if (!helperName) continue;
      const helperStart =
        blockStart + helperMatch.index + helperMatch[0].indexOf(helperName);
      const helperEnd = helperStart + helperName.length;
      const isNested = helperMatch[0].startsWith('(');
      const className = isNested
        ? 'cm-hbsHelper cm-hbsHelperNested'
        : 'cm-hbsHelper';
      ranges.push({
        from: helperStart,
        to: helperEnd,
        deco: Decoration.mark({ class: className }),
      });
    }

    // Strings
    STRING_RE.lastIndex = 0;
    while ((helperMatch = STRING_RE.exec(blockContent))) {
      const from = blockStart + helperMatch.index;
      const to = from + helperMatch[0].length;
      ranges.push({
        from,
        to,
        deco: Decoration.mark({ class: 'cm-hbsString' }),
      });
    }

    // Numbers
    NUMBER_RE.lastIndex = 0;
    while ((helperMatch = NUMBER_RE.exec(blockContent))) {
      const from = blockStart + helperMatch.index;
      const to = from + helperMatch[0].length;
      ranges.push({
        from,
        to,
        deco: Decoration.mark({ class: 'cm-hbsNumber' }),
      });
    }

    // Hash param names (key in key=value)
    PARAM_RE.lastIndex = 0;
    while ((helperMatch = PARAM_RE.exec(blockContent))) {
      const key = helperMatch[1];
      if (!key) continue;
      const keyStart =
        blockStart + helperMatch.index + helperMatch[0].indexOf(key);
      const keyEnd = keyStart + key.length;
      ranges.push({
        from: keyStart,
        to: keyEnd,
        deco: Decoration.mark({ class: 'cm-hbsParam' }),
      });
    }
  }

  ranges.sort((a, b) => a.from - b.from || a.to - b.to);
  const builder = new RangeSetBuilder<Decoration>();
  for (const { from, to, deco } of ranges) {
    builder.add(from, to, deco);
  }
  return builder.finish();
}

export function handlebarsMarkdown() {
  const handlebarsOverlay = ViewPlugin.fromClass(
    class {
      decorations = Decoration.none;

      constructor(view: EditorView) {
        this.decorations = buildDecorations(view);
      }

      update(update: ViewUpdate) {
        if (update.docChanged || update.viewportChanged) {
          this.decorations = buildDecorations(update.view);
        }
      }
    },
    {
      decorations: (v) => v.decorations,
    }
  );

  return [
    markdown(),
    handlebarsOverlay,
    EditorView.baseTheme({
      '.cm-hbsBracket': {
        color: 'var(--chakra-colors-purple-600)',
        fontWeight: '700',
      },
      '.cm-hbsParen': {
        color: 'var(--chakra-colors-purple-500)',
      },
      '.cm-hbsHelper': {
        color: 'var(--chakra-colors-blue-700)',
        fontWeight: '700',
      },
      '.cm-hbsHelperNested': {
        color: 'var(--chakra-colors-blue-600)',
        fontStyle: 'italic',
      },
      '.cm-hbsString': {
        color: 'var(--chakra-colors-green-700)',
      },
      '.cm-hbsNumber': {
        color: 'var(--chakra-colors-orange-600)',
      },
      '.cm-hbsParam': {
        color: 'var(--chakra-colors-teal-700)',
      },
    }),
  ];
}
